import { useEffect, useMemo, useState } from 'react'
import { usePublicClient, useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import bribeAbi from '@/lib/abis/BribeMarket.json'
import gaugeAbi from '@/lib/abis/GaugeController.json'
import { getLogsChunked } from '@/lib/getLogsChunked'
import { POLL_SLOW } from '@/lib/utils'

const BRIBED_EVENT = bribeAbi.find((x) => x.type === 'event' && x.name === 'Bribed')
const ERC20_META = [
  { type: 'function', name: 'symbol', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
]
const notDeployed = (a) => !IS_LIVE || (a || '').toLowerCase() === ZERO_ADDR

/// Bribe claims (BribeMarket). Anyone can `bribe(farmId, token, amount)` a gauge in ANY token, so
/// there is no on-chain enumeration of (gauge, epoch, token) - this reconstructs the set from indexed
/// `Bribed` logs (the same way a subgraph would, windowed via getLogsChunked from the deploy block),
/// keeps only ENDED epochs (claimable only once the tally is frozen), then reads
/// `claimable(farmId, epoch, token, user)` per tuple and groups what the holder is owed by gauge.
/// Claim side only - posting a bribe is a deferred surface (VE33-FRONTEND-PLAN.md).
export function useBribes(user) {
  const bm = ADDRESSES.BRIBE_MARKET
  const gc = ADDRESSES.GAUGE_CONTROLLER
  const client = usePublicClient()
  const enabled = !notDeployed(bm) && !notDeployed(gc) && !!user

  const [tuples, setTuples] = useState([]) // { farmId: bigint, epoch: number, token: address }
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!enabled || !client) { setTuples([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const fromBlock = ADDRESSES.BRIBE_MARKET_DEPLOY_BLOCK
          ? BigInt(ADDRESSES.BRIBE_MARKET_DEPLOY_BLOCK)
          : ADDRESSES.LENDING_POOL_DEPLOY_BLOCK ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK) : 0n
        const logs = await getLogsChunked(client, { address: bm, event: BRIBED_EVENT, fromBlock })
        const seen = new Map()
        for (const l of logs) {
          const key = `${l.args.farmId}-${Number(l.args.epoch)}-${l.args.token.toLowerCase()}`
          if (!seen.has(key)) seen.set(key, { farmId: l.args.farmId, epoch: Number(l.args.epoch), token: l.args.token })
        }
        if (!cancelled) setTuples([...seen.values()])
      } catch (e) {
        console.error('useBribes: log scan failed', e)
        if (!cancelled) setTuples([])
      }
    })()
    return () => { cancelled = true }
  }, [enabled, client, bm, nonce])

  const { data: cur } = useReadContract({
    address: gc, abi: gaugeAbi, functionName: 'currentEpoch',
    query: { enabled, refetchInterval: POLL_SLOW },
  })
  const curEpoch = Number(cur ?? 0n)
  const ended = useMemo(() => tuples.filter((t) => curEpoch > 0 && t.epoch < curEpoch), [tuples, curEpoch])

  const { data: claimReads, refetch: refetchClaim } = useReadContracts({
    query: { enabled: enabled && ended.length > 0, refetchInterval: POLL_SLOW },
    contracts: ended.map((t) => ({ address: bm, abi: bribeAbi, functionName: 'claimable', args: [t.farmId, BigInt(t.epoch), t.token, user] })),
  })

  const tokens = useMemo(() => [...new Set(ended.map((t) => t.token.toLowerCase()))], [ended])
  const { data: metaReads } = useReadContracts({
    query: { enabled: enabled && tokens.length > 0 },
    contracts: tokens.flatMap((a) => [
      { address: a, abi: ERC20_META, functionName: 'symbol' },
      { address: a, abi: ERC20_META, functionName: 'decimals' },
    ]),
  })
  const meta = useMemo(() => {
    const m = {}
    tokens.forEach((a, i) => {
      m[a] = { symbol: metaReads?.[i * 2]?.result ?? (a.slice(0, 6) + '…'), decimals: Number(metaReads?.[i * 2 + 1]?.result ?? 18) }
    })
    return m
  }, [tokens, metaReads])

  const groups = useMemo(() => {
    const byFarm = new Map()
    ended.forEach((t, i) => {
      const amt = claimReads?.[i]?.result ?? 0n
      if (amt <= 0n) return
      const fid = t.farmId.toString()
      if (!byFarm.has(fid)) byFarm.set(fid, { farmId: t.farmId, claims: [], byToken: {} })
      const g = byFarm.get(fid)
      g.claims.push({ epoch: t.epoch, token: t.token, amount: amt })
      const tk = t.token.toLowerCase()
      g.byToken[tk] = (g.byToken[tk] ?? 0n) + amt
    })
    return [...byFarm.values()]
  }, [ended, claimReads])

  const refetch = async () => { setNonce((n) => n + 1); await refetchClaim().catch(() => {}) }

  return { groups, meta, hasAny: groups.length > 0, notDeployed: !enabled, refetch }
}
